/**
 * TextBubble — per-type subcomponent for `ChatMessageType.text` messages.
 *
 * Flutter source of truth:
 *  - Own variant: `currentUserMessageTypeText` (`chat_screen.dart:1396-1456`).
 *  - Peer variant: `peerUserMessageTypeText` (`chat_screen.dart:2508-2563`).
 *
 * Both variants are structurally identical — the only differences are:
 *  - Bubble background color (`AppColors.ownMessageColor` vs
 *    `AppColors.peerMessageColor`).
 *  - Side alignment (own is right-padded; peer is left-padded).
 *  - Tap-to-retry: on `chatMessage.success === false`, the own variant fires
 *    `sendMessage(chatMessage)` instead of toggling the timestamp
 *    (`chat_screen.dart:1400-1403`). The peer variant has no retry path.
 *  - Asymmetric corners: own's `bottomRight` collapses to 4.0 on the
 *    last-of-run bubble; peer's `bottomLeft` collapses to 4.0 on the
 *    last-of-run bubble. The dispatcher pre-computes `isLastOfRun`; the
 *    corner-radii helper from `MessageBubble.tsx` resolves the four CSS
 *    corners based on `isOwn` + `isLastOfRun`.
 *
 * Tap-to-toggle timestamp (`chat_screen.dart:1405-1414` /
 * `:2512-2521`): the bubble does NOT own the "only one open at a time"
 * invariant. The chat hook resets every other timestamp to `false` then
 * flips the tapped one. This component just calls
 * `onToggleTimestamp(message.timestamp)`.
 *
 * URL linkification (`chat_screen.dart:1438-1450` / `:2545-2557`): Flutter
 * uses the `Linkify` widget with `linkStyle: TextStyle(color: white)`. Web
 * uses the project's `linkifyText` util which returns a `ReactNode[]` of
 * text + anchor spans. The util emits unstyled `<a>` tags; anchors pick up
 * the bubble's `color: theme.white` via CSS inheritance, so the white
 * link color comes for free from the bubble's text color.
 *
 * Theme tokens:
 *  - Own bubble background → `theme.chat.ownMessage` (added in Task 24, mapped
 *    to Flutter's `AppColors.ownMessageColor = activeRed = #D10033`).
 *  - Peer bubble background → `theme.chat.peerMessage` (added in Task 24,
 *    web-side aligned to `theme.surface` (deliberate deviation from
 *    Flutter's `AppColors.peerMessageColor` on `kIsWeb` = `0xB3FFFFFF`)).
 *  - Text color → `theme.white` (Flutter's `AppStyles.chatStyle.color =
 *    AppColors.white` at `app_styles.dart:38-41`). Both own and peer text
 *    are white in Flutter; we keep parity.
 *
 * Responsive tokens:
 *  - Font size → `responsive.font.lg` (= 16px on mobile; Flutter uses 15px
 *    at `app_styles.dart:39`). The nearest token on the project's scale —
 *    same mapping used by `SendErrorBanner`. Documented as a UI deviation
 *    per `presentation.md`.
 *  - Padding → `responsive.spacing.sm` on all four sides (Flutter uses 10
 *    on all four sides at `chat_screen.dart:1431-1437`; the `sm` token is
 *    8 — the closest available; this is a small UI deviation).
 *  - Border radii → resolved by `getBubbleCornerRadii` (see
 *    `MessageBubble.tsx` for the full mapping: Flutter 14 → `lg`,
 *    Flutter 4 → `sm`).
 *
 * Stop click propagation: anchor `onClick` already calls
 * `event.stopPropagation()` inside `linkifyText`, so clicking a URL does
 * not also toggle the timestamp / fire retry. The outer bubble click
 * handler is on the bubble container itself (not the document) so that's
 * sufficient.
 *
 * Container element: the bubble is a `<div role="button" tabIndex={0}>`
 * instead of a `<button>` so the linkified `<a>` children produce valid
 * HTML (the spec disallows nesting interactive content inside a button).
 * Keyboard accessibility is preserved via an `onKeyDown` handler that
 * fires `handleTap` on `Enter` / `Space`.
 */

import React from 'react';
import { ChatMessageType } from '@/domain/enums/chatMessageType';
import { useTheme } from '@/presentation/contexts/ThemeContext';
import { useResponsiveDesign } from '@/presentation/responsiveDesign/responsiveDesign';
import { linkifyText } from '@/presentation/utils/linkifyText';
import { ChatUtils } from '@/domain/utils/chatUtils';
import {
  registerBubble,
  type BubbleSubcomponentProps,
} from '../../registry/bubbleRegistry';
import { useEnterOrSpaceActivation } from '@/presentation/hooks/useEnterOrSpaceActivation';
import { qaAttr } from '@/presentation/utils/qaAttr';

export const TextBubble: React.FC<BubbleSubcomponentProps> = ({
  message,
  isOwn,
  isLastOfRun,
  onToggleTimestamp,
  onRetry,
}) => {
  const { theme } = useTheme();
  const responsive = useResponsiveDesign();

  const corners = ChatUtils.getBubbleCornerRadii({
    isOwn,
    isLastOfRun,
    runMidRadius: responsive.borderRadius.lg,
    runEndRadius: responsive.borderRadius.sm,
  });

  // Own-only tap-to-retry on failed sends. Mirrors
  // `chat_screen.dart:1400-1403` — peer text bubbles have no retry path
  // (`chat_screen.dart:2510-2522`), so this branch is gated on `isOwn`.
  const handleTap = (): void => {
    if (isOwn && message.success === false) {
      onRetry(message);
      return;
    }
    onToggleTimestamp(message.timestamp);
  };

  const backgroundColor = isOwn ? theme.chat.ownMessage : theme.chat.peerMessage;

  const handleKeyDown = useEnterOrSpaceActivation<HTMLDivElement>(handleTap);

  return (
    <div
      {...qaAttr({ id: `chat-bubble-text-${message.documentId}` })}
      role="button"
      tabIndex={0}
      onClick={handleTap}
      onKeyDown={handleKeyDown}
      style={{
        backgroundColor,
        borderTopLeftRadius: corners.topLeft,
        borderTopRightRadius: corners.topRight,
        borderBottomLeftRadius: corners.bottomLeft,
        borderBottomRightRadius: corners.bottomRight,
        paddingTop: responsive.spacing.sm,
        paddingBottom: responsive.spacing.sm,
        paddingLeft: responsive.spacing.sm,
        paddingRight: responsive.spacing.sm,
        // Flutter wraps the text bubble in `Flexible` (`chat_screen.dart:1397
        // / 2509`). On the web the bubble's row container caps its width;
        // the bubble itself just needs to break long text correctly.
        textAlign: 'left',
        cursor: 'pointer',
        color: theme.white,
        fontSize: responsive.font.lg,
        fontWeight: responsive.fontWeight.regular,
        // Long words / URLs must wrap inside the bubble width (Flutter's
        // `Text` widget wraps by default; web needs the explicit hint).
        wordBreak: 'break-word',
        whiteSpace: 'pre-wrap',
        // Keep the bubble from growing beyond its row container.
        maxWidth: '100%',
        // Web-only addition (no Flutter parity): Flutter's `Linkify` lets
        // very short text shrink the bubble to its natural width, but with
        // CSS line-height + vertical padding a 1-2 char message renders
        // taller than it is wide. Floor the bubble width so short bubbles
        // stay roughly square. See `responsive.chat.bubbleMinWidth`.
        minWidth: responsive.chat.bubbleMinWidth,
        // Without `box-sizing: border-box`, `minWidth` + horizontal padding
        // would compound; `border-box` makes the min match the rendered box.
        boxSizing: 'border-box',
      }}
    >
      {linkifyText(message.content)}
    </div>
  );
};

// Register with the dispatcher so `MessageBubble` routes
// `ChatMessageType.text` to this component. See `MessageBubble.tsx` for the
// registry contract.
registerBubble(ChatMessageType.text, TextBubble);
